/**
 * Shared API types for request/response contracts
 */

// ---------------------------------------------------------------------------
// Domain enums & derived types — canonical source is @floline/shared.
// Re-exported here so existing consumers of @floline/api-client/types
// continue to work without import-path changes.
// ---------------------------------------------------------------------------
export {
  BODY_ZONES,
  type BodyZone,
  BODY_SIDES,
  type BodySide,
  BODY_SURFACES,
  type BodySurface,
  BODY_REGIONS,
  type BodyRegion,
  LATERALITIES,
  type Laterality,
  EVENT_TYPES,
  type EventType,
  TIMES_OF_DAY,
  type TimeOfDay,
  DOSE_UNITS,
  type DoseUnit,
  CONFIDENCE_LEVELS,
  type ConfidenceLevel,
  TREND_DIRECTIONS,
  type TrendDirection,
  ANALYSIS_WINDOWS,
  type AnalysisWindow,
  EXPORT_FORMATS,
  type ExportFormat,
  DOCTOR_VISIT_STATUSES,
  type DoctorVisitStatus,
  DOCTOR_VISIT_ITEM_TYPES,
  type DoctorVisitItemType,
  DOCTOR_VISIT_ITEM_SOURCES,
  type DoctorVisitItemSource,
} from "@floline/shared";

// Also re-export domain items that api-client doesn't use locally but
// consumers may have been pulling transitively through shared:
export {
  PAIN_QUALITIES,
  type PainQuality,
  ENTRY_METHODS,
  type EntryMethod,
  PAIN_SCALE,
} from "@floline/shared";

// Local import of shared types used within interfaces below.
import type {
  BodyZone,
  BodySide,
  BodySurface,
  BodyRegion,
  Laterality,
  EventType,
  TimeOfDay,
  ConfidenceLevel,
  TrendDirection,
  AnalysisWindow,
  ExportFormat,
  DoctorVisitStatus,
  DoctorVisitItemType,
  DoctorVisitItemSource,
} from "@floline/shared";

/**
 * Generic API response wrapper with data payload
 */
export interface ApiResponse<T> {
  data: T;
  meta?: {
    timestamp: string;
  };
}

/**
 * Standardized error response structure
 */
export interface ApiErrorResponse {
  error: string;
  code: string;
  status: number;
  details?: unknown;
}

/**
 * User representation in the system
 */
export interface User {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
}

/** Supported body diagram models */
export type BodyModel = "male" | "female";

/** Supported application languages */
export type AppLanguage = "en" | "fi";

/** Supported temperature units */
export type TemperatureUnit = "c" | "f";

/** Supported date display formats */
export type DateFormat = "locale" | "dmy" | "mdy" | "ymd";

/** User-configurable settings */
export interface UserSettings {
  bodyModel: BodyModel;
  language: AppLanguage;
  /** Preferred AI check-in language (null = follow application language). */
  aiCheckInLanguage: AppLanguage | null;
  dateFormat: DateFormat;
  temperatureUnit: TemperatureUnit;
  weatherOverlayEnabled: boolean;
  /** ISO timestamp when onboarding was completed (null = not completed). */
  onboardingCompletedAt: string | null;
}

/** Request body for updating user settings */
export interface UpdateUserSettingsRequest {
  bodyModel?: BodyModel;
  language?: AppLanguage;
  /** Preferred AI check-in language (null = follow application language). */
  aiCheckInLanguage?: AppLanguage | null;
  dateFormat?: DateFormat;
  temperatureUnit?: TemperatureUnit;
  weatherOverlayEnabled?: boolean;
  /** Mark onboarding as completed (server stamps completion time). */
  onboardingComplete?: boolean;
}

/** User profile values editable in settings */
export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
}

/** Request body for updating profile values */
export interface UpdateUserProfileRequest {
  firstName: string;
  lastName: string;
  email: string;
  /** Required when changing the email address */
  currentEmail?: string;
}

/**
 * Session information containing user and expiration
 */
export interface Session {
  user: User;
  expiresAt: string;
}

/**
 * Health check response from the API
 */
export interface HealthCheckResponse {
  status: "ok" | "degraded" | "error";
  version: string;
  timestamp: string;
}

/**
 * Type guard to check if a response is an error
 */
export function isApiError(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === "object" &&
    response !== null &&
    "error" in response &&
    "code" in response &&
    "status" in response
  );
}

/**
 * Helper to create a successful API response
 */
export function createApiResponse<T>(
  data: T,
  includeTimestamp = true
): ApiResponse<T> {
  return {
    data,
    ...(includeTimestamp && { meta: { timestamp: new Date().toISOString() } }),
  };
}

// === Pain Entry Types ===

/** Pain quality entry */
export interface PainQualityEntry {
  kind: "standardized" | "freeform";
  value: string;
}

/** Pain location in a request */
export interface PainLocationInput {
  zone?: BodyZone;
  side?: BodySide;
  surface?: BodySurface;
  region: BodyRegion;
  laterality?: Laterality;
  detail?: string;
  intensityScore: number;
  qualities?: PainQualityEntry[];
}

// === Medication Intake Types ===

/** Medication intake input for requests */
export interface MedicationIntakeInput {
  name: string;
  doseValue?: number;
  doseUnit?: string;
  timeOfDay?: TimeOfDay;
  notes?: string;
  /** Link to canonical user_medications row (for active-medication intakes) */
  userMedicationId?: string;
  /** Link to one-off medication_taken event created from check-in UI */
  relatedEventId?: string;
  /** True when the row was auto-populated from an active medication course */
  fromActiveMedication?: boolean;
}

/** Medication intake response */
export interface MedicationIntakeResponse {
  id: string;
  dailyRecordId: string;
  medicationId: string | null;
  /** Link to canonical user_medications row (null when not linked to a medication course) */
  userMedicationId: string | null;
  /** Link to one-off medication_taken event (null when not linked) */
  relatedEventId: string | null;
  name: string;
  doseValue: number | null;
  doseUnit: string | null;
  route: string | null;
  timeOfDay: TimeOfDay;
  notes: string | null;
}

// === Medication Suggestions Types ===

/**
 * Source indicating where a medication suggestion came from.
 * - `catalog`                 – matched from the medication catalogue translations.
 * - `history_user_medication` – an existing user_medications row.
 */
export type MedicationSuggestionSource =
  | "catalog"
  | "history_user_medication";

/** A single medication suggestion (history + catalogue). */
export interface MedicationSuggestion {
  /** The medication name as previously recorded (backward-compat) */
  name: string;
  /** How many times this medication has been recorded (0 for catalog-only) */
  count: number;
  /** Canonical (language-neutral) name from the catalogue, if available */
  canonicalName: string | null;
  /** Locale-specific display name (from catalogue translation or user history) */
  displayName: string;
  /** Locale the displayName belongs to (e.g. "en", "fi") */
  locale: string | null;
  /** Where this suggestion originated */
  source: MedicationSuggestionSource;
}

/** Response for medication suggestions endpoint */
export interface MedicationSuggestionsResponse {
  data: MedicationSuggestion[];
}

// === Functional Impact Types ===

/**
 * Functional impact input - BPI interference dimensions (0-10)
 * All fields optional; only provided dimensions are stored.
 */
export interface FunctionalImpactInput {
  generalActivity?: number;
  mood?: number;
  mobility?: number;
  work?: number;
  relationships?: number;
  sleep?: number;
  enjoymentOfLife?: number;
  notes?: string;
}

/**
 * Functional impact in API responses
 */
export interface FunctionalImpactResponse {
  id: string;
  dailyRecordId: string;
  generalActivity: number | null;
  mood: number | null;
  mobility: number | null;
  work: number | null;
  relationships: number | null;
  sleep: number | null;
  enjoymentOfLife: number | null;
  notes: string | null;
}

/** Request body for creating/updating pain entries */
export interface CreatePainEntryRequest {
  recordDate: string;
  timezone?: string;
  painLocations: PainLocationInput[];
  medications?: MedicationIntakeInput[];
  functionalImpact?: FunctionalImpactInput;
  notes?: string;
  tags?: string[];
}

/** Request body for updating a pain location */
export interface UpdatePainLocationRequest {
  zone?: BodyZone;
  side?: BodySide;
  surface?: BodySurface;
  laterality?: Laterality;
  detail?: string | null;
  intensityScore?: number;
  qualities?: PainQualityEntry[] | null;
}

/** Pain location in a response */
export interface PainLocationResponse {
  id: string;
  dailyRecordId: string;
  zone?: BodyZone;
  side?: BodySide;
  surface?: BodySurface;
  region: BodyRegion;
  laterality: Laterality;
  detail: string | null;
  intensityScore: number;
  qualities: PainQualityEntry[] | null;
  updatedAt: string;
}

/** Daily record response */
export interface DailyRecordResponse {
  id: string;
  userId: string;
  recordDate: string;
  timezone: string;
  entryMethod: "text" | "audio" | "manual" | "mixed";
  notes: string | null;
  tags: string[] | null;
  extractedBy: "user" | "ai" | "mixed" | null;
  overallConfidence: number | null;
  reviewState: "unreviewed" | "user_confirmed" | "user_edited" | null;
  createdAt: string;
  updatedAt: string;
  painLocations: PainLocationResponse[];
  medications?: MedicationIntakeResponse[];
  functionalImpact?: FunctionalImpactResponse | null;
}

/** Response for listing daily records with pagination */
export interface ListDailyRecordsResponse {
  data: DailyRecordResponse[];
  meta: {
    timestamp: string;
    total: number;
    limit: number;
    offset: number;
  };
}

// === Dashboard Types ===

/** Dashboard summary for today */
export interface TodaySummary {
  date: string;
  recordId: string | null;
  locationCount: number;
  averageIntensity: number | null;
  lastCheckInTime: string | null;
  painLocations: Array<{
    zone?: BodyZone;
    side?: BodySide;
    surface?: BodySurface;
    region: BodyRegion;
    laterality: Laterality;
    intensityScore: number;
  }>;
}

/** Week summary for dashboard */
export interface WeekSummary {
  startDate: string;
  endDate: string;
  daysLogged: number;
  totalDays: 7;
  weeksInRowLogged: number;
  longestWeeksInRowLogged: number;
  averageIntensity: number | null;
  dailyIntensities: Array<{
    date: string;
    averageIntensity: number | null;
    locationCount: number;
    painLocations: Array<{
      zone?: BodyZone;
      side?: BodySide;
      surface?: BodySurface;
      region: BodyRegion;
      laterality: Laterality;
      intensityScore: number;
    }>;
  }>;
}

/** Dashboard response */
export interface DashboardResponse {
  today: TodaySummary;
  thisWeek: WeekSummary;
  hasAnyData: boolean;
}

// === Event Types ===
// Following specs/08_events_highlights.md

/** Attachment metadata */
export interface EventAttachment {
  type: string;
  url: string;
  name: string;
}

/** Event response from API */
export interface EventResponse {
  id: string;
  userId: string;
  eventType: EventType;
  title: string;
  startDate: string;
  endDate: string | null;
  notes: string | null;
  /** Optional location ID (e.g. travel destination) */
  locationId: string | null;
  relatedMedicationId: string | null;
  /** Parent event ID for medication lifecycle tracking (dose changes, switches) */
  parentEventId: string | null;
  confidenceLevel: ConfidenceLevel;
  attachments: EventAttachment[] | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  version: number;
}

/** Request body for creating an event */
export interface CreateEventRequest {
  eventType: EventType;
  title: string;
  startDate: string;
  endDate?: string;
  notes?: string;
  /** Optional location ID (e.g. travel destination) */
  locationId?: string;
  relatedMedicationId?: string;
  /** Parent event ID for linking dose changes/switches to original medication event */
  parentEventId?: string;
  confidenceLevel?: ConfidenceLevel;
  attachments?: EventAttachment[];
}

/** Request body for updating an event */
export interface UpdateEventRequest {
  eventType?: EventType;
  title?: string;
  startDate?: string;
  endDate?: string | null;
  notes?: string | null;
  /** Optional location ID (e.g. travel destination). Use null to clear */
  locationId?: string | null;
  relatedMedicationId?: string | null;
  parentEventId?: string | null;
  confidenceLevel?: ConfidenceLevel;
  attachments?: EventAttachment[] | null;
}

/** Response for listing events with pagination */
export interface ListEventsResponse {
  data: EventResponse[];
  meta: {
    timestamp: string;
    total: number;
    limit: number;
    offset: number;
  };
}

// === Active Medication Types ===

/**
 * An active medication derived from `user_medications`.
 *
 * All fields sourced from the `user_medications` table.
 */
export interface ActiveMedication {
  /** The user_medications row id */
  userMedicationId: string;
  /** User-facing medication name (e.g., "Ibuprofen 200 mg") */
  displayName: string;
  /** Numeric dose value (e.g. 200) */
  doseValue: number | null;
  /** Dose unit (e.g. "mg") */
  doseUnit: string | null;
  /** Route of administration (e.g. "oral") */
  route: string | null;
  /** When the medication was started */
  startDate: string;
  /** When the medication was stopped (null = still active) */
  endDate: string | null;
  /** Whether to include this medication in trend visualisations */
  showInTrends: boolean;
}

/** Response for active medications endpoint */
export interface ActiveMedicationsResponse {
  data: ActiveMedication[];
}

// === User Medication Types (CRUD) ===
// Following docs/medication-events-overhaul-v2.md §3.1

/** Medication source indicating how it was created */
export type MedicationSource = "manual" | "ai_suggested" | "ai_applied";

/** A user medication row from user_medications table */
export interface UserMedicationResponse {
  id: string;
  userId: string;
  displayName: string;
  catalogMedicationId: string | null;
  doseValue: number | null;
  doseUnit: string | null;
  route: string | null;
  startDate: string;
  endDate: string | null;
  source: MedicationSource;
  showInTrends: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Request body for creating a new user medication */
export interface CreateMedicationRequest {
  displayName: string;
  startDate: string;
  catalogMedicationId?: string;
  doseValue?: number;
  doseUnit?: string;
  route?: string;
  endDate?: string;
  notes?: string;
  /** Whether this medication should appear on the trends chart (default: true) */
  showInTrends?: boolean;
}

/** Request body for updating a user medication */
export interface UpdateMedicationRequest {
  displayName?: string;
  doseValue?: number | null;
  doseUnit?: string | null;
  route?: string | null;
  endDate?: string | null;
  showInTrends?: boolean;
  notes?: string | null;
  /**
   * Optional effective date (YYYY-MM-DD) for side-effect events emitted by
   * the PATCH handler (e.g. medication_dose_change).
   * When omitted, defaults to today's date.
   */
  effectiveDate?: string;
}

/** Request body for switching from one medication to another */
export interface SwitchMedicationRequest {
  toDisplayName: string;
  switchDate: string;
  toCatalogMedicationId?: string;
  doseValue?: number;
  doseUnit?: string;
  route?: string;
  notes?: string;
}

/** Response for switching medications */
export interface SwitchMedicationResponse {
  sourceMedication: { id: string; endDate: string };
  newMedication: UserMedicationResponse;
}

/** Response for listing medications with pagination */
export interface ListMedicationsResponse {
  data: UserMedicationResponse[];
  meta: {
    timestamp: string;
    total: number;
    limit: number;
    offset: number;
  };
}

/** Request body for stopping a medication event */
export interface StopMedicationRequest {
  /** The date the medication was stopped (YYYY-MM-DD) */
  endDate: string;
  /** Optional notes about why the medication was stopped */
  notes?: string;
}

/** Request body for recording a dose change */
export interface DoseChangeRequest {
  /** New medication title with updated dose (e.g., "Ibuprofen 400mg") */
  newTitle: string;
  /** The date of the dose change (YYYY-MM-DD) */
  changeDate: string;
  /** Optional notes about the dose change */
  notes?: string;
}

// === Event Correlation Types ===
// Following specs/08_events_highlights.md

/** Metrics for a single comparison window (before or after) */
export interface WindowMetrics {
  avgPain: number | null;
  stdDev: number | null;
  dataPoints: number;
}

/** Complete correlation metrics for an event and window */
export interface CorrelationMetrics {
  eventId: string;
  eventDate: string;
  analysisWindowDays: AnalysisWindow;
  before: WindowMetrics;
  after: WindowMetrics;
  trendDirection: TrendDirection;
  calculatedAt: string;
}

/** Event correlation response from API */
export interface EventCorrelationResponse {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  during: WindowMetrics;
  metrics: {
    7?: CorrelationMetrics;
    14?: CorrelationMetrics;
    30?: CorrelationMetrics;
  };
  insufficientData: boolean;
  message?: string;
}

// === Audio Upload Types ===

/** Audio upload response from API */
export interface AudioUploadResponse {
  /** Unique blob ID (UUID) */
  blobId: string;
  /** Full blob path (userId/filename) */
  blobPath: string;
  /** Blob URL */
  blobUrl: string;
  /** Stored MIME type */
  mimeType: string;
  /** File size in bytes */
  size: number;
  /** Upload timestamp */
  uploadedAt: string;
}

// === Transcription Types ===

/** Supported audio formats for transcription */
export const TRANSCRIPTION_FORMATS = [
  "webm",
  "mp3",
  "mp4",
  "m4a",
  "wav",
  "ogg",
] as const;

export type TranscriptionFormat = (typeof TRANSCRIPTION_FORMATS)[number];

/** Request body for creating a transcription */
export interface CreateTranscriptionRequest {
  /** Language hint in ISO-639-1 format (e.g., "en") - improves accuracy */
  language?: string;
}

/** Transcription response from API */
export interface TranscriptionResponse {
  /** The transcribed text */
  text: string;
  /** Detected or specified language (ISO-639-1 code) */
  language: string | null;
  /** Audio duration in seconds */
  duration: number | null;
  /** Timestamp when transcription was performed */
  transcribedAt: string;
  /** Confidence score (0-1) if available */
  confidence: number | null;
}

/** Transcription error codes */
export const TRANSCRIPTION_ERROR_CODES = [
  "MISSING_AUDIO_DATA",
  "UNSUPPORTED_FORMAT",
  "INVALID_MIME_TYPE",
  "TRANSCRIPTION_FAILED",
  "SERVICE_UNAVAILABLE",
] as const;

export type TranscriptionErrorCode = (typeof TRANSCRIPTION_ERROR_CODES)[number];

// === AI Extraction Types ===

/** Extracted pain location from transcript */
export interface ExtractedPainLocation {
  zone?: BodyZone;
  side?: BodySide;
  surface?: BodySurface;
  /** Body region (matches BodyRegion enum) */
  region: BodyRegion;
  /** Laterality if specified */
  laterality: Laterality;
  /** Pain intensity 0-10 */
  intensityScore: number;
  /** Pain quality descriptors */
  qualities: string[];
  /** Additional detail text */
  detail: string | null;
  /** Extraction confidence 0-1 */
  confidence: number;
}

/** Extracted medication intake from transcript */
export interface ExtractedMedication {
  /** Medication name (normalized if possible) */
  name: string;
  /** Dose value if mentioned */
  doseValue: number | null;
  /** Dose unit if mentioned */
  doseUnit: string | null;
  /** Time of day if mentioned */
  timeOfDay: TimeOfDay;
  /** Extraction confidence 0-1 */
  confidence: number;
}

/** Event types that can be extracted by AI */
export const EXTRACTABLE_EVENT_TYPES = [
  // Medication Events
  "medication_start",
  "medication_dose_change",
  "medication_switch",
  // Medical Interventions
  "surgery_procedure",
  "injection",
  "therapy_session",
  // Health Status Changes
  "injury",
  "illness",
  "flare",
] as const;

export type ExtractableEventType = (typeof EXTRACTABLE_EVENT_TYPES)[number];

/** Extracted event from transcript */
export interface ExtractedEvent {
  /** Event type (medication, medical, health status only) */
  eventType: ExtractableEventType;
  /** Suggested title from user's words */
  title: string;
  /** Event date (ISO string) */
  date: string;
  /** Event end date for duration events */
  endDate?: string;
  /** Related medication name if applicable */
  relatedMedication: string | null;
  /** Additional notes */
  notes?: string;
  /** Confidence level */
  confidenceLevel: ConfidenceLevel;
  /** Extraction confidence 0-1 */
  confidence: number;
}

/** Pain locations grouped by date */
export interface ExtractedPainLocationsByDate {
  date: string;
  locations: ExtractedPainLocation[];
}

/** Medications grouped by date */
export interface ExtractedMedicationsByDate {
  date: string;
  medications: ExtractedMedication[];
}

/** Complete extraction result from a transcript */
export interface ExtractionResult {
  /** Extracted pain locations grouped by date */
  painLocations: ExtractedPainLocationsByDate[];
  /** Extracted medications grouped by date */
  medications: ExtractedMedicationsByDate[];
  /** Suggested events (high confidence only) */
  events: ExtractedEvent[];
  /** Freeform notes extracted */
  notes: string | null;
  /** Overall extraction confidence */
  overallConfidence: number;
  /** Low confidence items that need user review */
  lowConfidenceItems: string[];
  /** Whether clarification is recommended */
  needsClarification: boolean;
  /** Suggested clarification questions */
  clarificationQuestions: string[];
}

/** Request body for extracting data from transcript */
export interface ExtractRequest {
  /** The transcript text to extract from */
  transcript: string;
  /** Reference date for relative dates (YYYY-MM-DD), defaults to today */
  referenceDate?: string;
  /** User's timezone */
  timezone?: string;
  /** User's existing medication list for normalization */
  knownMedications?: string[];
  /** Context about current update set for multi-turn conversation */
  context?: string;
}

/** Extraction error codes */
export const EXTRACTION_ERROR_CODES = [
  "MISSING_TRANSCRIPT",
  "TRANSCRIPT_TOO_LONG",
  "EXTRACTION_FAILED",
  "INVALID_JSON_RESPONSE",
  "SERVICE_UNAVAILABLE",
] as const;

export type ExtractionErrorCode = (typeof EXTRACTION_ERROR_CODES)[number];

/** Confidence thresholds for extraction */
export const CONFIDENCE_THRESHOLDS = {
  /** High confidence: Auto-populate, show in review (editable) */
  HIGH: 0.9,
  /** Medium confidence: Auto-populate with visual indicator */
  MEDIUM: 0.7,
  /** Low confidence: Do NOT auto-populate, present as suggestion */
  LOW: 0.5,
} as const;

// === Insights Types ===
// Following specs/10_trends_insights_summaries.md

/**
 * Insight type enum
 * Per spec 10, permitted insight categories
 */
export const INSIGHT_TYPES = [
  "trend_observation",
  "pattern_identification",
  "correlation_observation",
  "progress_indicator",
  "focus_suggestion",
] as const;

export type InsightType = (typeof INSIGHT_TYPES)[number];

/**
 * Insight priority levels
 */
export const INSIGHT_PRIORITIES = ["high", "medium", "low"] as const;
export type InsightPriority = (typeof INSIGHT_PRIORITIES)[number];

/**
 * Supporting data for insight context
 */
export interface InsightData {
  /** Percentage change (for trends) */
  percentChange?: number;
  /** Before/after averages (for correlations) */
  beforeAvg?: number;
  afterAvg?: number;
  /** Most frequent day(s) */
  peakDays?: string[];
  /** Streak count */
  streakDays?: number;
  /** Location name */
  location?: string;
  /** Date range analyzed */
  dateRange?: {
    start: string;
    end: string;
  };
}

/**
 * Single generated insight
 */
export interface Insight {
  /** Unique identifier */
  id: string;
  /** Type of insight */
  type: InsightType;
  /** Human-readable message */
  message: string;
  /** Priority level */
  priority: InsightPriority;
  /** Date insight was generated */
  generatedAt: string;
  /** Supporting data (optional) */
  data?: InsightData;
}

/**
 * Insights generation result
 */
export interface InsightsResult {
  insights: Insight[];
  generatedAt: string;
  dateRange: {
    start: string;
    end: string;
  };
}

/** Valid date range options for insights */
export const INSIGHTS_DATE_RANGES = ["7d", "30d", "90d", "180d", "365d"] as const;
export type InsightsDateRange = (typeof INSIGHTS_DATE_RANGES)[number];

/** Parameters for getting insights */
export interface GetInsightsParams {
  /** Date range for analysis (default: "30d") */
  dateRange?: InsightsDateRange;
  /** UI language for localized insight messages */
  language?: AppLanguage;
}

/** Response metadata for insights endpoint */
export interface InsightsResponseMeta {
  dateRange: InsightsDateRange;
  startDate: string;
  endDate: string;
  recordCount: number;
  eventCount: number;
  /** Number of insights that are new (not dismissed) */
  newInsightsCount: number;
  /** Whether there are any new (not dismissed) insights */
  hasNewInsights: boolean;
  timestamp: string;
}

/** Full insights API response */
export interface InsightsResponse {
  data: InsightsResult;
  meta: InsightsResponseMeta;
}

// === Dismissed Insights Types ===

/** Response for listing dismissed insights */
export interface DismissedInsightsResponse {
  data: {
    dismissedInsightIds: string[];
    count: number;
  };
  meta: {
    timestamp: string;
  };
}

/** Request body for dismissing an insight */
export interface DismissInsightRequest {
  insightId: string;
}

/** Response after dismissing an insight */
export interface DismissInsightResponse {
  data: {
    id: string;
    insightId: string;
    dismissedAt: string;
  };
  meta: {
    timestamp: string;
  };
}

// === PDF Export Types ===
// Following specs/10_trends_insights_summaries.md

/** Request body for generating an export */
export interface GenerateExportRequest {
  /** Start date (YYYY-MM-DD) */
  startDate: string;
  /** End date (YYYY-MM-DD) */
  endDate: string;
  /** Export format */
  format: ExportFormat;
  /** Include detailed daily data tables (default: true) */
  includeDetailedData?: boolean;
  /** Include body diagram visualizations (default: true, PDF only) */
  includeBodyDiagrams?: boolean;
  /** Omit free-text notes for privacy (default: false) */
  omitNotes?: boolean;
  /** Remove patient name from report (default: false) */
  removePatientName?: boolean;
}

/** Export summary statistics */
export interface ExportStats {
  /** Date range covered */
  dateRange: {
    start: string;
    end: string;
  };
  /** Total number of check-ins */
  totalCheckIns: number;
  /** Days in the selected range */
  daysInRange: number;
  /** Days with at least one check-in */
  daysWithData: number;
  /** Average pain level (0-10) */
  avgPainLevel: number | null;
  /** Minimum pain level recorded */
  minPainLevel: number | null;
  /** Maximum pain level recorded */
  maxPainLevel: number | null;
  /** Median pain level */
  medianPainLevel: number | null;
  /** Standard deviation of pain levels */
  painStdDev: number | null;
}

/** Pain location frequency in export */
export interface ExportLocationFrequency {
  zone?: BodyZone;
  side?: BodySide;
  surface?: BodySurface;
  /** Body region */
  region: BodyRegion;
  /** Laterality */
  laterality: Laterality;
  /** Number of days this location was reported */
  daysReported: number;
  /** Percentage of days with this location */
  percentOfDays: number;
  /** Average intensity when reported */
  avgIntensity: number;
  /** Common pain qualities */
  qualities: string[];
}

/** Medication summary in export */
export interface ExportMedicationSummary {
  /** Medication name */
  name: string;
  /** First date taken in range */
  firstTaken: string;
  /** Last date taken in range */
  lastTaken: string;
  /** Total days medication was taken */
  daysTaken: number;
  /** Average pain level on days medication was taken */
  avgPainDuring: number | null;
}

/** Event summary in export */
export interface ExportEventSummary {
  /** Event ID */
  id: string;
  /** Event type */
  eventType: EventType;
  /** Event title */
  title: string;
  /** Event date */
  date: string;
  /** Whether there's sufficient data for before/after analysis */
  hasBeforeAfterData: boolean;
}

/** Daily data row in detailed export */
export interface ExportDailyData {
  /** Date (YYYY-MM-DD) */
  date: string;
  /** Average pain level for the day */
  avgPainLevel: number | null;
  /** Pain locations recorded */
  locations: Array<{
    zone?: BodyZone;
    side?: BodySide;
    surface?: BodySurface;
    region: BodyRegion;
    laterality: Laterality;
    intensity: number;
    qualities: string[];
  }>;
  /** Medications taken */
  medications: Array<{
    name: string;
    doseValue: number | null;
    doseUnit: string | null;
    timeOfDay: TimeOfDay;
  }>;
  /** Notes (may be null if omitted for privacy) */
  notes: string | null;
}

/** Complete export data response */
export interface ExportDataResponse {
  /** When the export was generated */
  generatedAt: string;
  /** Summary statistics */
  stats: ExportStats;
  /** Pain location frequencies */
  locationFrequencies: ExportLocationFrequency[];
  /** Medication summaries */
  medicationSummaries: ExportMedicationSummary[];
  /** Event summaries */
  eventSummaries: ExportEventSummary[];
  /** Detailed daily data (if includeDetailedData=true) */
  dailyData: ExportDailyData[];
  /** Medical disclaimer text */
  disclaimerText: string;
}

/** Text summary export response */
export interface TextSummaryResponse {
  /** Plain text summary */
  text: string;
  /** When generated */
  generatedAt: string;
  /** Date range covered */
  dateRange: {
    start: string;
    end: string;
  };
}

/** Export error codes */
export const EXPORT_ERROR_CODES = [
  'INVALID_DATE_RANGE',
  'DATE_RANGE_TOO_LARGE',
  'INSUFFICIENT_DATA',
  'EXPORT_GENERATION_FAILED',
  'UNSUPPORTED_FORMAT',
] as const;

export type ExportErrorCode = (typeof EXPORT_ERROR_CODES)[number];

// === Location Types ===
// Following docs/weather-event-correlation.md + apps/api/src/routes/locations.ts

/** A user geographic location (city-level) from the API */
export interface LocationResponse {
  id: string;
  userId: string;
  label: string;
  latitude: number;
  longitude: number;
  country: string | null;
  countryCode: string | null;
  admin1: string | null;
  timezone: string | null;
  openMeteoId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /** Whether this location is the user's current default */
  isDefault: boolean;
}

/** Request body for creating a location (city geocode) */
export interface CreateLocationRequest {
  /** City name to geocode via Open-Meteo */
  query: string;
}

/** Request body for updating a location */
export interface UpdateLocationRequest {
  /** Rename the location label */
  label?: string;
  /** Set this location as the default */
  isDefault?: boolean;
}

/** Response for listing locations */
export interface ListLocationsResponse {
  data: LocationResponse[];
  meta: {
    timestamp: string;
    total: number;
  };
}

/** A valid city suggestion for location search */
export interface LocationSearchResult {
  id: number;
  name: string;
  label: string;
  latitude: number;
  longitude: number;
  country: string | null;
  countryCode: string | null;
  admin1: string | null;
  timezone: string | null;
}

/** Response for searching valid cities */
export interface SearchLocationsResponse {
  data: LocationSearchResult[];
  meta: {
    timestamp: string;
    total: number;
  };
}

/** Parameters for getting weather data */
export interface GetWeatherParams {
  /** Start date (YYYY-MM-DD) */
  startDate: string;
  /** End date (YYYY-MM-DD) */
  endDate: string;
}

// === Weather Types ===
// Following docs/weather-event-correlation.md

/** A single day of weather data returned by GET /api/weather */
export interface WeatherDailyPoint {
  /** Date in YYYY-MM-DD format */
  date: string;
  /** Effective location ID for this day */
  locationId: string;
  /** Daily high temperature in Celsius */
  temperatureHighC: number | null;
  /** Daily low temperature in Celsius */
  temperatureLowC: number | null;
  /** Daily mean temperature in Celsius */
  temperatureMeanC: number | null;
  /** Relative humidity percentage */
  humidity: number | null;
  /** Atmospheric pressure in hPa */
  pressureHpa: number | null;
  /** Precipitation in mm */
  precipitationMm: number | null;
  /** Wind speed in km/h */
  windSpeedKmh: number | null;
  /** Cloud cover percentage */
  cloudCoverPercent: number | null;
  /** WMO weather code */
  weatherCode: number | null;
  /** Data source (e.g. "open-meteo") */
  source: string;
}

/** Response for listing weather daily data */
export interface ListWeatherResponse {
  data: WeatherDailyPoint[];
  meta: {
    timestamp: string;
    total: number;
  };
}

// === Doctor Visit Types ===
// Following docs/doctor-visit-prep-redesign.md

/** Source reference for AI-drafted items */
export type DoctorVisitItemSourceRef =
  | { kind: "event"; id: string }
  | { kind: "daily_record"; date: string }
  | { kind: "medication"; id: string }
  | { kind: "freeform"; hash: string };

/** Doctor visit response from API */
export interface DoctorVisitResponse {
  id: string;
  userId: string;
  visitDate: string;
  visitTime: string | null;
  status: DoctorVisitStatus;
  visitType: string;
  clinician: string | null;
  clinic: string | null;
  agendaNotes: string | null;
  packetDefaultStartDate: string | null;
  packetDefaultEndDate: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Doctor visit item response from API */
export interface DoctorVisitItemResponse {
  id: string;
  doctorVisitId: string;
  sortOrder: number;
  type: DoctorVisitItemType;
  text: string;
  linkedEventId: string | null;
  linkedUserMedicationId: string | null;
  linkedRecordDate: string | null;
  resolutionText: string | null;
  source: DoctorVisitItemSource;
  sourceRef: DoctorVisitItemSourceRef | null;
  createdAt: string;
  updatedAt: string;
}

/** Request body for creating a doctor visit */
export interface CreateDoctorVisitRequest {
  visitDate: string;
  visitType: string;
  visitTime?: string;
  status?: DoctorVisitStatus;
  clinician?: string;
  clinic?: string;
  agendaNotes?: string;
}

/** Request body for updating a doctor visit */
export interface UpdateDoctorVisitRequest {
  visitDate?: string;
  visitType?: string;
  visitTime?: string | null;
  status?: DoctorVisitStatus;
  clinician?: string | null;
  clinic?: string | null;
  agendaNotes?: string | null;
}

/** Request body for creating a visit item */
export interface CreateDoctorVisitItemRequest {
  text: string;
  type: DoctorVisitItemType;
  sortOrder?: number;
  source?: DoctorVisitItemSource;
  sourceRef?: DoctorVisitItemSourceRef | null;
}

/** Request body for updating a visit item */
export interface UpdateDoctorVisitItemRequest {
  text?: string;
  type?: DoctorVisitItemType;
  sortOrder?: number;
  resolutionText?: string | null;
  source?: DoctorVisitItemSource;
  sourceRef?: DoctorVisitItemSourceRef | null;
}

/** Response for listing doctor visits with pagination */
export interface ListDoctorVisitsResponse {
  data: DoctorVisitResponse[];
  meta: {
    timestamp: string;
    total: number;
    limit: number;
    offset: number;
  };
}

// === Suggested Timeframe Types ===

/** Response from GET /api/doctor-visits/suggested-timeframe */
export interface SuggestedTimeframeResponse {
  startDate: string;
  endDate: string;
  basis:
    | "last_completed_visit_by_type_and_clinician"
    | "last_completed_visit_by_type"
    | "last_completed_visit_global"
    | "default_30_days";
  lastCompletedVisit?: {
    id: string;
    visitDate: string;
    visitType: string;
    clinician: string | null;
  };
  effectiveTimezone: string;
}

// === Visit Context Types ===

/** A single key event summary in the visit context */
export interface KeyEventSummary {
  id: string;
  eventType: string;
  title: string;
  startDate: string;
  endDate: string | null;
  notes: string | null;
}

/** Functional impact averages (BPI dimensions, 0-10, null = no data) */
export interface FunctionalImpactSummary {
  generalActivity: number | null;
  mood: number | null;
  mobility: number | null;
  work: number | null;
  sleep: number | null;
  enjoymentOfLife: number | null;
  daysRecorded: number;
}

/** Medication change during the date range */
export interface MedicationChange {
  name: string;
  changeType: "started" | "stopped" | "dose_change";
  date: string;
  details?: string;
}

/** Pain stats computed by shared aggregation */
export interface PainStats {
  mean: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
  stddev: number | null;
  count: number;
}

/** Location frequency from shared aggregation */
export interface LocationFrequency {
  zone?: string;
  side?: string;
  surface?: string;
  region: string;
  laterality: string;
  count: number;
  percentOfDays: number;
}

/** Quality frequency from shared aggregation */
export interface QualityFrequency {
  quality: string;
  count: number;
}

/** Medication summary from shared aggregation */
export interface MedicationSummary {
  name: string;
  daysTaken: number;
  avgPainDuring: number | null;
  avgPainWithout: number | null;
}

/** Daily pain data point */
export interface DailyPainPoint {
  date: string;
  avgPain: number | null;
  minPain: number | null;
  maxPain: number | null;
}

/** Response from GET /api/doctor-visits/:id/context */
export interface VisitContextResponse {
  firstRecordDate: string | null;
  lastRecordDate: string | null;
  appliedRange: { startDate: string; endDate: string };
  effectiveTimezone: string;
  painStats: PainStats;
  daysTracked: number;
  daysInRange: number;
  topLocations: LocationFrequency[];
  topQualities: QualityFrequency[];
  functionalImpact: FunctionalImpactSummary | null;
  medicationSummaries: MedicationSummary[];
  medicationChanges: MedicationChange[];
  suggestedKeyEvents: KeyEventSummary[];
  dailyPainSeries: DailyPainPoint[];
}

// === Export Types ===

/** Request body for POST /api/doctor-visits/:id/export */
export interface ExportDoctorVisitRequest {
  startDate: string;
  endDate: string;
  includeSections: Record<string, boolean>;
  format?: "pdf" | "text";
  includeAppendix?: boolean;
  omitNotes?: boolean;
  removePatientName?: boolean;
}

// === Consent Types (GDPR Art. 9/13) ===

/** Valid consent type identifiers */
export type ConsentType =
  | "core_health_processing"
  | "audio_transcription"
  | "ai_extraction"
  | "ai_insights";

/** A single consent entry in the API response */
export interface ConsentStateEntry {
  consentType: ConsentType;
  given: boolean;
  consentTextVersion: string;
  updatedAt: string;
}

/** Response shape for GET /api/consents */
export interface ConsentsResponse {
  consents: ConsentStateEntry[];
}

/** A single consent decision in the PUT /api/consents request */
export interface ConsentDecision {
  given: boolean;
  consentTextVersion: string;
  consentText?: string;
}

/** Request shape for PUT /api/consents */
export interface UpdateConsentsRequest {
  decisions: Record<string, ConsentDecision>;
}

/** A single event in the consent audit history */
export interface ConsentHistoryEntry {
  id: string;
  consentType: ConsentType;
  action: "give" | "withdraw";
  consentTextVersion: string;
  consentText: string | null;
  actor: string;
  createdAt: string;
  metadata: Record<string, unknown> | null;
}

/** Response shape for GET /api/consents/history */
export interface ConsentHistoryResponse {
  events: ConsentHistoryEntry[];
}
